import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Tab,
  Nav,
  Spinner,
  ListGroup,
  Form,
  Modal,
} from "react-bootstrap";
import {
  ArrowLeft,
  Briefcase,
  GeoAlt,
  Rulers,
  Hash,
  FileText,
  Clock,
  Calendar,
  Calculator,
  CheckCircle,
  ListCheck,
  FileEarmarkText,
  Folder,
  House,
  Building,
  Layers,
  Grid3x3,
  Person,
  Gear,
  PencilSquare,
  Plus,
  Trash,
  Save,
  Download,
  Upload,
  Share,
  Printer,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://34.206.193.218:4001/api/admin";

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  
  const token = localStorage.getItem("adminToken") || localStorage.getItem("token");

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      // You'll need to create this API endpoint or use existing one
      // For now, let's simulate with existing data
      const res = await axios.get(
        `${API_BASE}/data/getProjectsByUser/someUserId`, // This needs to be updated
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (res.data.success && res.data.projects && res.data.projects.length > 0) {
        // Find the specific project by ID
        const foundProject = res.data.projects.find(p => p._id === projectId);
        if (foundProject) {
          setProject(foundProject);
        } else {
          toast.error("Project not found");
          navigate(-1);
        }
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project details");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers for different features
  const handleFeatureNavigation = (feature) => {
    switch(feature) {
      case 'bid-estimate':
        navigate(`/project/${projectId}/bid-estimate`);
        break;
      case 'notes':
        navigate(`/project/${projectId}/notes`);
        break;
      case 'todo':
        navigate(`/project/${projectId}/todo`);
        break;
      case 'documents':
        navigate(`/project/${projectId}/documents`);
        break;
      case 'tasks':
        navigate(`/project/${projectId}/tasks`);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading project details...</span>
      </div>
    );
  }

  if (!project) {
    return (
      <Container className="py-5 text-center">
        <h4 className="mb-3">Project not found</h4>
        <Button variant="primary" onClick={() => navigate(-1)}>
          <ArrowLeft className="me-2" />
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3"
              >
                <ArrowLeft className="me-2" />
                Back to Projects
              </Button>
              <h2 className="fw-bold mb-2">
                <Briefcase className="me-3" />
                {project.name}
              </h2>
              <div className="d-flex align-items-center">
                <Badge bg="primary" className="px-3 py-2 me-3">
                  <GeoAlt className="me-2" />
                  {project.projectLocation}
                </Badge>
                <Badge bg="secondary" className="px-3 py-2">
                  <Hash className="me-2" />
                  Lot: {project.lotNo}
                </Badge>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button variant="outline-primary" onClick={() => setShowEditModal(true)}>
                <PencilSquare className="me-2" />
                Edit Project
              </Button>
              <Button variant="success">
                <Save className="me-2" />
                Save Changes
              </Button>
            </div>
          </div>

          {/* TABS NAVIGATION */}
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link eventKey="overview">
                  <Briefcase className="me-2" />
                  Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="bid-estimate" onClick={() => handleFeatureNavigation('bid-estimate')}>
                  <Calculator className="me-2" />
                  Bid Estimate
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="notes" onClick={() => handleFeatureNavigation('notes')}>
                  <FileEarmarkText className="me-2" />
                  Notes
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="todo" onClick={() => handleFeatureNavigation('todo')}>
                  <CheckCircle className="me-2" />
                  To-Do List
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents" onClick={() => handleFeatureNavigation('documents')}>
                  <Folder className="me-2" />
                  Documents
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tasks" onClick={() => handleFeatureNavigation('tasks')}>
                  <ListCheck className="me-2" />
                  Tasks
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="settings">
                  <Gear className="me-2" />
                  Settings
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="overview">
                <Row>
                  <Col md={8}>
                    {/* Project Description */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <Card.Body>
                        <h5 className="d-flex align-items-center mb-3">
                          <FileText className="me-2 text-primary" />
                          Project Description
                        </h5>
                        <div className="p-3 bg-light rounded">
                          <p className="mb-0">{project.description || "No description available for this project."}</p>
                        </div>
                      </Card.Body>
                    </Card>
                    
                    {/* Measurements */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <Card.Body>
                        <h5 className="d-flex align-items-center mb-4">
                          <Rulers className="me-2 text-primary" />
                          Project Measurements
                        </h5>
                        <Row className="g-3">
                          {[
                            { label: "Living Area", value: project.livingSqft, icon: House, color: "primary", bg: "bg-primary bg-opacity-10" },
                            { label: "Slab Area", value: project.slabSqft, icon: Layers, color: "warning", bg: "bg-warning bg-opacity-10" },
                            { label: "Roof Area", value: project.areaRoofSqft, icon: Building, color: "info", bg: "bg-info bg-opacity-10" },
                            { label: "Flatwork Area", value: project.flatworkSqft, icon: Grid3x3, color: "success", bg: "bg-success bg-opacity-10" },
                          ].map((item, index) => (
                            <Col xs={6} md={3} key={index}>
                              <Card className={`border-0 ${item.bg} h-100`}>
                                <Card.Body className="text-center py-4">
                                  <div className="mb-3">
                                    <item.icon size={32} className={`text-${item.color}`} />
                                  </div>
                                  <div className="fw-bold display-6 text-dark mb-1">{item.value}</div>
                                  <small className="text-muted">{item.label}</small>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>

                    {/* Project Timeline */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <Card.Body>
                        <h5 className="d-flex align-items-center mb-4">
                          <Clock className="me-2 text-primary" />
                          Project Timeline
                        </h5>
                        <Row>
                          <Col md={6}>
                            <div className="p-3 border rounded mb-3">
                              <small className="text-muted d-block mb-1">Created Date</small>
                              <div className="fw-bold">
                                <Calendar className="me-2" />
                                {new Date(project.createdAt).toLocaleDateString()}
                              </div>
                              <small className="text-muted">
                                {new Date(project.createdAt).toLocaleTimeString()}
                              </small>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="p-3 border rounded">
                              <small className="text-muted d-block mb-1">Last Updated</small>
                              <div className="fw-bold">
                                <Calendar className="me-2" />
                                {new Date(project.updatedAt).toLocaleDateString()}
                              </div>
                              <small className="text-muted">
                                {new Date(project.updatedAt).toLocaleTimeString()}
                              </small>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col md={4}>
                    {/* Quick Actions */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <Card.Body>
                        <h5 className="d-flex align-items-center mb-3">
                          <Gear className="me-2 text-primary" />
                          Quick Actions
                        </h5>
                        <ListGroup variant="flush">
                          <ListGroup.Item 
                            action 
                            className="d-flex align-items-center py-3 border-bottom"
                            onClick={() => handleFeatureNavigation('bid-estimate')}
                          >
                            <Calculator className="me-3 text-success" />
                            <div>
                              <div className="fw-medium">Bid Estimate</div>
                              <small className="text-muted">Create or view bid estimates</small>
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item 
                            action 
                            className="d-flex align-items-center py-3 border-bottom"
                            onClick={() => handleFeatureNavigation('notes')}
                          >
                            <FileEarmarkText className="me-3 text-info" />
                            <div>
                              <div className="fw-medium">Notes</div>
                              <small className="text-muted">Add or view project notes</small>
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item 
                            action 
                            className="d-flex align-items-center py-3 border-bottom"
                            onClick={() => handleFeatureNavigation('todo')}
                          >
                            <CheckCircle className="me-3 text-warning" />
                            <div>
                              <div className="fw-medium">To-Do List</div>
                              <small className="text-muted">Manage project tasks</small>
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item 
                            action 
                            className="d-flex align-items-center py-3 border-bottom"
                            onClick={() => handleFeatureNavigation('documents')}
                          >
                            <Folder className="me-3 text-primary" />
                            <div>
                              <div className="fw-medium">Documents</div>
                              <small className="text-muted">Upload or view documents</small>
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item 
                            action 
                            className="d-flex align-items-center py-3"
                            onClick={() => handleFeatureNavigation('tasks')}
                          >
                            <ListCheck className="me-3 text-secondary" />
                            <div>
                              <div className="fw-medium">Tasks</div>
                              <small className="text-muted">Assign and track tasks</small>
                            </div>
                          </ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>
                    
                    {/* Project Information */}
                    <Card className="border-0 shadow-sm">
                      <Card.Body>
                        <h5 className="d-flex align-items-center mb-3">
                          <Hash className="me-2 text-primary" />
                          Project Information
                        </h5>
                        
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Project ID</small>
                          <div className="p-2 bg-light rounded">
                            <code className="small">{project._id}</code>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Created By User ID</small>
                          <div className="d-flex align-items-center p-2 bg-light rounded">
                            <Person className="me-2" />
                            <code className="small">{project.createdBy || "Unknown"}</code>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Project Status</small>
                          <Badge bg={project.deleted ? "danger" : "success"} className="px-3 py-2">
                            {project.deleted ? "Deleted" : "Active"}
                          </Badge>
                        </div>
                        
                        <div className="mt-4">
                          <Button variant="outline-primary" className="w-100 mb-2">
                            <Download className="me-2" />
                            Export Project Data
                          </Button>
                          <Button variant="outline-secondary" className="w-100">
                            <Printer className="me-2" />
                            Print Summary
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab.Pane>
              
              {/* Other tab content would go here */}
              <Tab.Pane eventKey="settings">
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h5>Project Settings</h5>
                    <p>Settings content will go here...</p>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      {/* EDIT MODAL */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <PencilSquare className="me-2" />
            Edit Project: {project.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control 
                    defaultValue={project.name} 
                    placeholder="Enter project name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control 
                    defaultValue={project.projectLocation} 
                    placeholder="Enter project location"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Lot Number</Form.Label>
                  <Form.Control 
                    defaultValue={project.lotNo} 
                    placeholder="Enter lot number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Status</Form.Label>
                  <Form.Select defaultValue={project.deleted ? "deleted" : "active"}>
                    <option value="active">Active</option>
                    <option value="deleted">Deleted</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                defaultValue={project.description} 
                placeholder="Enter project description"
              />
            </Form.Group>
            
            <h6 className="mb-3">Measurements</h6>
            <Row className="g-3">
              <Col xs={6} md={3}>
                <Form.Group>
                  <Form.Label>Living Sqft</Form.Label>
                  <Form.Control 
                    type="number" 
                    defaultValue={project.livingSqft} 
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={3}>
                <Form.Group>
                  <Form.Label>Slab Sqft</Form.Label>
                  <Form.Control 
                    type="number" 
                    defaultValue={project.slabSqft} 
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={3}>
                <Form.Group>
                  <Form.Label>Roof Sqft</Form.Label>
                  <Form.Control 
                    type="number" 
                    defaultValue={project.areaRoofSqft} 
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
              <Col xs={6} md={3}>
                <Form.Group>
                  <Form.Label>Flatwork Sqft</Form.Label>
                  <Form.Control 
                    type="number" 
                    defaultValue={project.flatworkSqft} 
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => {
            toast.success("Project updated successfully!");
            setShowEditModal(false);
            fetchProjectDetails(); // Refresh data
          }}>
            <Save className="me-2" />
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProjectDetails;